import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import axiosInstance from '../../api/axios';
import Pagination from '../../components/Pagination';
import SortableTableHeader from '../../components/SortableTableHeader';
import { CopyHash } from '../../features/web3/forex/components/CopyHash';
import { bscAddressUrl } from '../../lib/format';

const CreditsDepositHistory = () => {
  const { token } = useSelector((state) => state.auth);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  const [filters, setFilters] = useState({
    page: 1,
    pageSize: 10,
    username: '',
    startDate: '',
    endDate: '',
    order: ''
  });
  const [tempFilters, setTempFilters] = useState({
    page: 1,
    pageSize: 10,
    username: '',
    startDate: '',
    endDate: '',
    order: ''
  });

  const [sort, setSort] = useState({ key: '', direction: 'asc' });
  const [sortedData, setSortedData] = useState([]);
  const [pagination, setPagination] = useState({ currentPage: 1, totalPages: 1, totalRows: 0 });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const params = { page: filters.page, pageSize: filters.pageSize };
      if (filters.username) params.username = filters.username;
      if (filters.startDate) params.startDate = filters.startDate;
      if (filters.endDate) params.EndDate = filters.endDate;
      if (filters.order) params.order = filters.order;

      const response = await axiosInstance.get('/api/admin/wallets/credits/deposits-history', {
        headers: { token },
        params
      });

      if (response.data.success) {
        const newData = response.data.data.data;
        setData(newData);
        setSortedData(newData);
        setPagination({
          currentPage: parseInt(response.data.data.currentPage) || 1,
          totalPages: response.data.data.totalPages || 1,
          totalRows: response.data.data.totalRows || 0
        });
      }
    } catch (error) {
      console.error('Error fetching credits deposit history:', error);
    } finally {
      setLoading(false);
    }
  }, [token, filters]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (!data.length) return;
    const sorted = [...data].sort((a, b) => {
      if (!sort.key) return 0;
      let aValue = a[sort.key] ?? '';
      let bValue = b[sort.key] ?? '';

      if (sort.key === 'amount' || sort.key === 'total') {
        aValue = parseFloat(aValue) || 0;
        bValue = parseFloat(bValue) || 0;
        return sort.direction === 'asc' ? aValue - bValue : bValue - aValue;
      }

      if (sort.key === 'createdAt' || sort.key === 'updatedAt') {
        aValue = new Date(aValue).getTime();
        bValue = new Date(bValue).getTime();
        return sort.direction === 'asc' ? aValue - bValue : bValue - aValue;
      }

      aValue = String(aValue).toLowerCase();
      bValue = String(bValue).toLowerCase();
      return sort.direction === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
    });
    setSortedData(sorted);
  }, [data, sort]);

  const handleTempFilterChange = (key, value) => {
    setTempFilters(prev => ({ ...prev, [key]: value }));
  };

  const applyFilters = () => {
    setFilters({ ...tempFilters, page: 1 });
  };

  const clearFilters = () => {
    const defaults = { page: 1, pageSize: 10, username: '', startDate: '', endDate: '', order: '' };
    setFilters(defaults);
    setTempFilters(defaults);
  };

  const handlePageChange = (newPage) => {
    setFilters(prev => ({ ...prev, page: newPage }));
    setTempFilters(prev => ({ ...prev, page: newPage }));
  };

  const handleSort = (key) => {
    setSort(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const formatDate = (dateString) => new Date(dateString).toLocaleString();

  const exportToExcel = async () => {
    try {
      const params = { page: 1, pageSize: 10000 };
      if (filters.username) params.username = filters.username;
      if (filters.startDate) params.startDate = filters.startDate;
      if (filters.endDate) params.EndDate = filters.endDate;
      if (filters.order) params.order = filters.order;

      const response = await axiosInstance.get('/api/admin/wallets/credits/deposits-history', {
        headers: { token },
        params
      });

      const exportData = response.data.data.data;
      if (!exportData.length) {
        alert('No data to export');
        return;
      }

      const headers = [
        'Username', 'Wallet Address', 'Transaction ID', 'Transaction Type',
        'Wallet Type', 'Amount', 'Total', 'Message', 'Created At', 'Updated At'
      ];
      const csvContent = [
        headers.join(','),
        ...exportData.map(row => [
          row.username || '',
          row.walletAddress || '',
          row.tranx_id || '',
          row.tranx_type || '',
          row.wallet_type || '',
          row.amount ?? '',
          row.total ?? '',
          `"${(row.message || '').replace(/"/g, '""')}"`,
          row.createdAt ? new Date(row.createdAt).toLocaleString() : '',
          row.updatedAt ? new Date(row.updatedAt).toLocaleString() : ''
        ].join(','))
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.setAttribute('href', URL.createObjectURL(blob));
      link.setAttribute('download', `credits_deposit_history_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error('Export failed:', err);
      alert('Export failed. Please try again.');
    }
  };

  return (
    <div>
      <div className="flex items-center gap-3 text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground mb-1">
        <span className="h-px w-6 bg-ginox-green" aria-hidden="true" />
        Finance
      </div>
      <h1 className="font-serif italic text-[45px] leading-[1.02] text-foreground">
        Credit Deposit <span className="text-gradient-ginox">Record</span>
      </h1>
      <p className="text-sm text-muted-foreground mb-6">View credits deposit transaction history</p>

      {/* Filters */}
      <div className="mb-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          <div>
            <label className="block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground mb-1.5">Username / Trx ID / WalletAddress</label>
            <input
              type="text"
              value={tempFilters.username}
              onChange={(e) => handleTempFilterChange('username', e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') applyFilters(); }}
              placeholder="Search…"
              className="w-full rounded-md border border-border/70 bg-white/[0.03] px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/50 focus:border-ginox-green/50 focus:outline-none focus:ring-1 focus:ring-ginox-green/30"
            />
          </div>

          <div>
            <label className="block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground mb-1.5">Start Date</label>
            <input
              type="date"
              value={tempFilters.startDate}
              onChange={(e) => handleTempFilterChange('startDate', e.target.value)}
              className="w-full rounded-md border border-border/70 bg-white/[0.03] px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/50 focus:border-ginox-green/50 focus:outline-none focus:ring-1 focus:ring-ginox-green/30"
            />
          </div>

          <div>
            <label className="block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground mb-1.5">End Date</label>
            <input
              type="date"
              value={tempFilters.endDate}
              onChange={(e) => handleTempFilterChange('endDate', e.target.value)}
              className="w-full rounded-md border border-border/70 bg-white/[0.03] px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/50 focus:border-ginox-green/50 focus:outline-none focus:ring-1 focus:ring-ginox-green/30"
            />
          </div>

          <div>
            <label className="block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground mb-1.5">Order</label>
            <select
              value={tempFilters.order}
              onChange={(e) => handleTempFilterChange('order', e.target.value)}
              className="w-full rounded-md border border-border/70 bg-white/[0.03] px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/50 focus:border-ginox-green/50 focus:outline-none focus:ring-1 focus:ring-ginox-green/30"
            >
              <option value="">Default</option>
              <option value="asc">Ascending</option>
              <option value="desc">Descending</option>
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground mb-1.5">Page Size</label>
            <select
              value={tempFilters.pageSize}
              onChange={(e) => handleTempFilterChange('pageSize', parseInt(e.target.value))}
              className="w-full rounded-md border border-border/70 bg-white/[0.03] px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/50 focus:border-ginox-green/50 focus:outline-none focus:ring-1 focus:ring-ginox-green/30"
            >
              <option value={10}>10</option>
              <option value={15}>15</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground mb-1.5">Page</label>
            <select
              value={tempFilters.page}
              onChange={(e) => handleTempFilterChange('page', parseInt(e.target.value))}
              className="w-full rounded-md border border-border/70 bg-white/[0.03] px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/50 focus:border-ginox-green/50 focus:outline-none focus:ring-1 focus:ring-ginox-green/30"
            >
              {Array.from({ length: pagination.totalPages }, (_, i) => (
                <option key={i + 1} value={i + 1}>{i + 1}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          <button onClick={applyFilters} className="rounded-md border border-ginox-green/60 bg-ginox-green/10 px-4 py-2 text-xs font-medium text-ginox-green hover:bg-ginox-green/20 transition-colors">
            Apply Filters
          </button>
          <button onClick={clearFilters} className="rounded-md border border-border/70 bg-white/[0.02] px-4 py-2 text-xs text-muted-foreground hover:bg-white/[0.05] hover:text-foreground transition-colors">
            Clear Filters
          </button>
          <button type="button" onClick={exportToExcel} className="rounded-md border border-border/70 bg-white/[0.02] px-4 py-2 text-xs text-muted-foreground hover:bg-white/[0.05] hover:text-foreground transition-colors">
            Export to Excel
          </button>
        </div>
      </div>

      {/* Data Table */}
      <div className="overflow-x-auto">
        {loading ? (
          <p className="py-12 text-center text-sm text-muted-foreground">Loading...</p>
        ) : (
          <>
            <table className="w-full border-separate border-spacing-0">
              <thead>
                <tr>
                  <SortableTableHeader
                    sortKey="username"
                    currentSort={sort}
                    onSort={handleSort}
                    className="border-b border-border/40 px-5 py-3.5 text-left text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground"
                  >
                    Username
                  </SortableTableHeader>
                  <SortableTableHeader
                    sortKey="tranx_id"
                    currentSort={sort}
                    onSort={handleSort}
                    className="border-b border-border/40 px-5 py-3.5 text-left text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground"
                  >
                    Transaction ID
                  </SortableTableHeader>
                  <SortableTableHeader
                    sortKey="tranx_type"
                    currentSort={sort}
                    onSort={handleSort}
                    className="border-b border-border/40 px-5 py-3.5 text-left text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground"
                  >
                    Type
                  </SortableTableHeader>
                  <SortableTableHeader
                    sortKey="wallet_type"
                    currentSort={sort}
                    onSort={handleSort}
                    className="border-b border-border/40 px-5 py-3.5 text-left text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground"
                  >
                    Wallet Type
                  </SortableTableHeader>
                  <SortableTableHeader
                    sortKey="amount"
                    currentSort={sort}
                    onSort={handleSort}
                    className="border-b border-border/40 px-5 py-3.5 text-left text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground"
                  >
                    Amount
                  </SortableTableHeader>
                  <SortableTableHeader
                    sortKey="total"
                    currentSort={sort}
                    onSort={handleSort}
                    className="border-b border-border/40 px-5 py-3.5 text-left text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground"
                  >
                    Total
                  </SortableTableHeader>
                  <SortableTableHeader
                    sortKey="message"
                    currentSort={sort}
                    onSort={handleSort}
                    className="border-b border-border/40 px-5 py-3.5 text-left text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground"
                  >
                    Message
                  </SortableTableHeader>
                  <SortableTableHeader
                    sortKey="createdAt"
                    currentSort={sort}
                    onSort={handleSort}
                    className="border-b border-border/40 px-5 py-3.5 text-left text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground"
                  >
                    Created At
                  </SortableTableHeader>
                  <SortableTableHeader
                    sortKey="updatedAt"
                    currentSort={sort}
                    onSort={handleSort}
                    className="border-b border-border/40 px-5 py-3.5 text-left text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground"
                  >
                    Updated At
                  </SortableTableHeader>
                </tr>
              </thead>
              <tbody>
                {sortedData.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="border-b border-border/40 px-5 py-3.5 text-sm text-center text-muted-foreground">
                      No data found
                    </td>
                  </tr>
                ) : (
                  sortedData.map((item) => (
                    <tr key={item._id} className="group hover:bg-white/[0.02] transition-colors">
                      <td className="border-b border-border/40 px-5 py-3.5 text-sm text-foreground font-mono">
                        <div>{item.username}</div>
                        <div className="text-xs text-muted-foreground/80">
                          <CopyHash value={item.walletAddress} href={bscAddressUrl(item.walletAddress)} head={6} tail={4} />
                        </div>
                      </td>
                      <td className="border-b border-border/40 px-5 py-3.5 text-sm text-foreground font-mono">{item.tranx_id}</td>
                      <td className="border-b border-border/40 px-5 py-3.5 text-sm text-foreground">{item.tranx_type}</td>
                      <td className="border-b border-border/40 px-5 py-3.5 text-sm text-foreground">{item.wallet_type}</td>
                      <td className="border-b border-border/40 px-5 py-3.5 text-sm text-foreground font-mono">{item.amount}</td>
                      <td className="border-b border-border/40 px-5 py-3.5 text-sm text-foreground font-mono">{item.total}</td>
                      <td className="border-b border-border/40 px-5 py-3.5 text-sm text-foreground max-w-xs truncate">{item.message}</td>
                      <td className="border-b border-border/40 px-5 py-3.5 text-sm text-foreground font-mono">{formatDate(item.createdAt)}</td>
                      <td className="border-b border-border/40 px-5 py-3.5 text-sm text-foreground font-mono">{formatDate(item.updatedAt)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>

          </>
        )}
      </div>
      <div className="border-t border-white/[0.08] px-4">
        <Pagination
          currentPage={pagination.currentPage}
          totalPages={pagination.totalPages}
          totalRows={pagination.totalRows}
          pageSize={filters.pageSize}
          onPageChange={handlePageChange}
        />
      </div>
    </div>
  );
};

export default CreditsDepositHistory;
